import React, { useState, useEffect } from 'react';
import { useCart } from './Cart';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { 
  Pane, 
  Button, 
  TextInput, 
  Tablist, 
  Tab, 
  Text, 
  Heading, 
  Paragraph, 
  Strong, 
  Spinner, 
  Alert 
} from 'evergreen-ui';
import './ProductDetails.css';
import AOS from "aos";
import "aos/dist/aos.css";

const ProductDetails = () => {
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('Description');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState(null);
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();

  useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init();
        AOS.refresh();
      }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      const id = searchParams.get('id');
      if (id) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BASE_URL}/products/${id}`);
          const data = await response.json();
          setProduct(data);
          setMainImage(`${process.env.REACT_APP_BASE_URL}/uploads/${data.image1}`);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching product:', error);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [searchParams]);

  const handleQuantityChange = (change) => {
    setQuantity(prev => Math.max(1, prev + change));
  };

  const handleAddToCart = async () => {
    try {
      const cartItem = {
        id: product.id,
        name: product.product_title,
        image: mainImage,
        code: product.sku,
        color: product.color || 'default',
        size: product.size || 'default',
        price: product.price,
      };

      addToCart(cartItem, quantity);

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/cart-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: product.id,
          quantity: quantity,
          color: product.color || 'default',
          size: product.size || 'default'
        }),
      });

      if (response.ok) {
        alert('Product added to cart!');
        setQuantity(1);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleThumbnailClick = (imageUrl) => {
    setMainImage(imageUrl);
  };

  const tabs = ['Description', 'Specifications', 'Shipping & Returns', 'Warranty'];

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
        <Spinner />
      </Pane>
    );
  }

  if (!product) {
    return (
      <Pane padding={16}>
        <Alert intent="danger" title="Product not found" />
      </Pane>
    );
  }

  return (
    <Pane className="productDetailContainer">
      <Pane className="productDetailMain">
        <Pane className="productDetailImages">
          <img 
            src={mainImage}
            alt={product.product_title}
            className="productDetailMainImage"
          />
          <Pane className="productDetailThumbnails">
            <img 
              src={`${process.env.REACT_APP_BASE_URL}/Uploads/${product.image1}`} 
              alt={`${product.product_title} view 1`}
              onClick={() => handleThumbnailClick(`${process.env.REACT_APP_BASE_URL}/Uploads/${product.image1}`)}
              className={mainImage === `${process.env.REACT_APP_BASE_URL}/Uploads/${product.image1}` ? 'active-thumbnail' : ''}
            />
            <img 
              src={`${process.env.REACT_APP_BASE_URL}/Uploads/${product.image2}`} 
              alt={`${product.product_title} view 2`}
              onClick={() => handleThumbnailClick(`${process.env.REACT_APP_BASE_URL}/Uploads/${product.image2}`)}
              className={mainImage === `${process.env.REACT_APP_BASE_URL}/Uploads/${product.image2}` ? 'active-thumbnail' : ''}
            />
          </Pane>
        </Pane>

        <Pane className="productDetailInfo">
          <Heading size={800}>{product.product_title}</Heading>
          <Text className="productDetailSku">SKU: {product.sku}</Text>
          <Heading size={700} className="productDetailPrice">${product.price}</Heading>

          <Pane className="productDetailOptionsGrid">
            <Pane className="productDetailOptionItem">
              <Text className="productDetailOptionLabel">Availability:</Text>
              <Text>{product.isAvailable ? 'In Stock' : 'Out of Stock'}</Text>
            </Pane>
            {product.color && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Color:</Text>
                <Text>{product.color}</Text>
              </Pane>
            )}
            {product.size && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Size:</Text>
                <Text>{product.size}</Text>
              </Pane>
            )}
            {product.material && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Material:</Text>
                <Text>{product.material}</Text>
              </Pane>
            )}
            {product.brand && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Brand:</Text>
                <Text>{product.brand}</Text>
              </Pane>
            )}
            {product.weight && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Weight:</Text>
                <Text>{product.weight}</Text>
              </Pane>
            )}
            {product.dimensions && (
              <Pane className="productDetailOptionItem">
                <Text className="productDetailOptionLabel">Dimensions:</Text>
                <Text>{product.dimensions}</Text>
              </Pane>
            )}
          </Pane>

          <Pane className="productDetailActionsWrapper">
            <Pane className="productDetailQuantity">
              <Button onClick={() => handleQuantityChange(-1)}>-</Button>
              <TextInput
                type="text"
                value={quantity}
                readOnly
                className="productDetailQuantityInput"
                width={60}
                textAlign="center"
              />
              <Button onClick={() => handleQuantityChange(1)}>+</Button>
            </Pane>

            <Pane className="productDetailActions">
              <Button
                appearance="primary"
                intent="success"
                onClick={handleAddToCart}
                disabled={!product.isAvailable}
                className="productDetailAddToCart"
              >
                Add to Cart
              </Button>
              <Button>
              <Link
    to="/cart"
    className="productDetailViewCart"
  >
    View in Cart
  </Link></Button>
            </Pane>
          </Pane>

          <Pane className="productDetailAdditionalInfo">
            <Paragraph><Strong>24/7 Customer Support:</Strong> +254720727776</Paragraph>
            <Paragraph><Strong>Delivery:</Strong> 3 - 5 Business Days</Paragraph>
          </Pane>
        </Pane>
      </Pane>

      <Tablist className="productDetailTabs">
        {tabs.map(tab => (
          <Tab
            key={tab}
            isSelected={activeTab === tab}
            onSelect={() => setActiveTab(tab)}
          >
            {tab}
          </Tab>
        ))}
      </Tablist>

      <Pane className="productDetailTabContent">
        {activeTab === 'Description' && (
          <Paragraph>{product.description || 'No description available'}</Paragraph>
        )}
        {activeTab === 'Specifications' && (
          <Pane>
            <Heading size={600}>Specifications</Heading>
            <ul>
              {product.color && <li><Strong>Color:</Strong> {product.color}</li>}
              {product.size && <li><Strong>Size:</Strong> {product.size}</li>}
              {product.material && <li><Strong>Material:</Strong> {product.material}</li>}
              {product.brand && <li><Strong>Brand:</Strong> {product.brand}</li>}
              {product.weight && <li><Strong>Weight:</Strong> {product.weight}</li>}
              {product.dimensions && <li><Strong>Dimensions:</Strong> {product.dimensions}</li>}
              {!product.color && !product.size && !product.material && 
               !product.brand && !product.weight && !product.dimensions && (
                <li>No specifications available</li>
              )}
            </ul>
          </Pane>
        )}
        {activeTab === 'Shipping & Returns' && (
          <Paragraph>Shipping information coming soon...</Paragraph>
        )}
        {activeTab === 'Warranty' && (
          <Paragraph>Warranty information coming soon...</Paragraph>
        )}
      </Pane>
    </Pane>
  );
};

export default ProductDetails;